import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { TimetableTripGroupsEntity, TimetableTripGroupsResponse, deserializeTimetableTripGroups } from 'src/entities/Timetable.entity';

import { TIMETABLES_QUERY_KEY } from './useTimetables.query';

/** 一覧のキー [TIMETABLES_QUERY_KEY] の下に置き、コースマスタの保存（invalidateCourseMaster）で一緒に捨てる */
export const TIMETABLE_TRIP_GROUPS_QUERY_KEY = [TIMETABLES_QUERY_KEY, 'tripGroups'] as const;
export const timetableTripGroupsQueryKey = (id: number, date: string) => [...TIMETABLE_TRIP_GROUPS_QUERY_KEY, id, date] as const;

// 便グループはダイヤ・コース・地点の編集とダイヤ保存で変わる（反映では変わらない）。編集・保存は invalidate で捨て、
// 反映は反映先を fetchQuery で取り直すので、固定ルート詳細と同じく staleTime を長めに取り、window focus での取り直しは止める
const STALE_TIME_MS = 5 * 60 * 1000;

/** queryFn。反映の後に useApplyTimetableMutation が fetchQuery で先読みするので hook の外に出す */
export const fetchTimetableTripGroups = async (timetableId: number, planningDate: string): Promise<TimetableTripGroupsEntity> =>
  deserializeTimetableTripGroups(
    (await axios.get<TimetableTripGroupsResponse>(`/api/v5/timetables/${timetableId}/trip_groups`, { params: { planning_date: planningDate } })).data,
  );

/**
 * その日のダイヤを便グループ（反映の車両枠）に展開したもの（GET /api/v5/timetables/:id/trip_groups。log_web_issues#365）。
 * 要確認の候補・本日なし・ダイヤ外・placeholder 行の材料。取れない間（未取得・404・失敗）はそれらを出さないだけで、配車の操作は止めない
 */
export const useTimetableTripGroupsQuery = (timetableId: number | null, planningDate: string) => useQuery<TimetableTripGroupsEntity, Error>({
  queryKey: timetableTripGroupsQueryKey(timetableId ?? 0, planningDate),
  queryFn: () => fetchTimetableTripGroups(timetableId ?? 0, planningDate),
  enabled: timetableId != null && Boolean(planningDate),
  staleTime: STALE_TIME_MS,
  refetchOnWindowFocus: false,
  // Why not 既定の retry: /courses で消されたダイヤの id を、一覧の取り直し中に useSelectedTimetableId が一瞬返す。
  // その 404 を 3 回待っても結果は変わらない。ネットワーク断などは既定どおり再試行する
  retry: (failureCount, error) => !(axios.isAxiosError(error) && error.response?.status === 404) && failureCount < 3,
});
